import { useState } from 'react';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, message, Modal, Select, Space, Tag, Typography, Upload } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { api, http } from '../../api/client';
import { mergeImportResolveResult } from './evalRelationsImportMerge';
import type {
  EvalImportPendingItem,
  EvalImportResolveItem,
  EvalImportResolveResponse,
  EvalImportResult,
} from './evalRelationsImportTypes';

interface Props {
  batchId: number | undefined;
  batchName: string | undefined;
}

export function EvalRelationsImportPanel({ batchId, batchName }: Props) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<UploadFile | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<EvalImportResult | null>(null);
  const [selections, setSelections] = useState<Record<string, number | undefined>>({});

  /**
   * 单入口两种形态：选了批次且批次已有关系 → 导出当前全部关系（改完可直接回导）；
   * 批次为空或未选批次 → 默认示例模版。
   */
  const downloadTemplate = async () => {
    try {
      const response = batchId
        ? await http.get('/admin/eval-tasks/export-import-format.xlsx', {
            params: { batchId },
            responseType: 'blob',
          })
        : await http.get('/admin/eval-tasks/import-template.xlsx', {
            responseType: 'blob',
          });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = batchId
        ? `评价关系导入-${batchName || batchId}.xlsx`
        : '评价关系导入模版.xlsx';
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      message.error(`模版下载失败：${(error as Error).message}`);
    }
  };

  const doImport = async () => {
    if (!file?.originFileObj) {
      message.warning('请先选择 .xls 或 .xlsx 文件');
      return;
    }
    if (!batchId) {
      message.error('请先在顶部选择批次');
      return;
    }
    setBusy(true);
    try {
      const formData = new FormData();
      formData.append('file', file.originFileObj as Blob);
      formData.append('batchId', String(batchId));
      const next = (await api.post('/admin/eval-tasks/import-excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })) as unknown as EvalImportResult;
      setResult(next);
      const initialSelections: Record<string, number | undefined> = {};
      next.pending.forEach((item) => {
        if (item.candidates.length === 1) {
          initialSelections[`${item.rowNo}-${item.slot}`] = item.candidates[0].empId;
        }
      });
      setSelections(initialSelections);
      message.success(
        `导入完成：新增 ${next.created} / 已存在跳过 ${next.skipped}${
          next.pending.length ? ` / 待确认 ${next.pending.length}` : ''
        }${next.errors.length ? ` / 错误 ${next.errors.length}` : ''}`,
      );
      queryClient.invalidateQueries({ queryKey: ['eval-grouped-by-evaluator'] });
      queryClient.invalidateQueries({ queryKey: ['eval-tasks-admin'] });
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      message.error(`导入失败：${err.response?.data?.message ?? err.message ?? '未知错误'}`);
    } finally {
      setBusy(false);
    }
  };

  const resolveMutation = useMutation({
    mutationFn: async (items: EvalImportResolveItem[]) =>
      (await api.post('/admin/eval-tasks/import-excel/resolve', {
        batchId,
        items,
      })) as unknown as EvalImportResolveResponse,
    onSuccess: (response: EvalImportResolveResponse, submitted) => {
      setResult((previous) =>
        previous ? mergeImportResolveResult(previous, response, submitted) : previous,
      );
      message.success(
        `已确认：新增 ${response.created ?? 0} / 跳过 ${response.skipped ?? 0}` +
          (response.invalid?.length ? ` / 无效 ${response.invalid.length}` : '') +
          (response.pending?.length ? ` / 仍待确认 ${response.pending.length}` : ''),
      );
      queryClient.invalidateQueries({ queryKey: ['eval-grouped-by-evaluator'] });
      queryClient.invalidateQueries({ queryKey: ['eval-tasks-admin'] });
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || error?.message || '待确认处理失败');
    },
  });

  const resolveOne = (item: EvalImportPendingItem) => {
    const sameRow = result?.pending.filter((pending) => pending.rowNo === item.rowNo) || [];
    const payload: EvalImportResolveItem = {
      rowNo: item.rowNo,
      row: item.row,
      evaluatorEmpId: selections[`${item.rowNo}-evaluator`],
      evaluateeEmpId: selections[`${item.rowNo}-evaluatee`],
    };
    if (
      (sameRow.some((pending) => pending.slot === 'evaluator') && !payload.evaluatorEmpId) ||
      (sameRow.some((pending) => pending.slot === 'evaluatee') && !payload.evaluateeEmpId)
    ) {
      message.warning(`第 ${item.rowNo} 行仍有槽位未选择员工`);
      return;
    }
    resolveMutation.mutate([payload]);
  };

  const resolveSelected = () => {
    const grouped = new Map<number, EvalImportResolveItem>();
    (result?.pending || []).forEach((item) => {
      const current = grouped.get(item.rowNo) || { rowNo: item.rowNo, row: item.row };
      if (item.slot === 'evaluator') current.evaluatorEmpId = selections[`${item.rowNo}-evaluator`];
      if (item.slot === 'evaluatee') current.evaluateeEmpId = selections[`${item.rowNo}-evaluatee`];
      grouped.set(item.rowNo, current);
    });
    const payload = Array.from(grouped.values()).filter(
      (item) => item.evaluatorEmpId || item.evaluateeEmpId,
    );
    if (!payload.length) {
      message.warning('请先选择至少 1 条待确认记录');
      return;
    }
    resolveMutation.mutate(payload);
  };

  return (
    <>
      <Button
        icon={<UploadOutlined />}
        onClick={() => {
          setFile(null);
          setResult(null);
          setSelections({});
          setOpen(true);
        }}
      >
        批量导入
      </Button>
      <Modal
        title="批量导入评价关系（Excel）"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={result?.pending.length ? resolveSelected : doImport}
        confirmLoading={busy || resolveMutation.isPending}
        okText={result?.pending.length ? '批量确认已选择' : '开始导入'}
        cancelText="关闭"
        destroyOnClose
        width={760}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <Alert
            type="info"
            showIcon
            message={
              <span>
                按模版列填写：<strong>评价人姓名 / 评价人工号（选填）/ 被评价人姓名 / 被评价人工号（选填）/ 评价关系 / 备注</strong>
              </span>
            }
            description={
              <ul style={{ margin: '4px 0 0 18px', padding: 0, fontSize: 12, color: '#475569' }}>
                <li>评价关系支持中文（自评 / 直属领导 / 斜线领导 / 本部门同事 / 跨部门同事 / 同级同事 / 下属 / 其他领导）或英文 enum（SELF / DIRECT / DIAGONAL / SAME_DEPT / CROSS_DEPT / PEER_LEADER / SUBORDINATE / OTHER_LEADER）。</li>
                <li>姓名重名会进入待确认清单，按候选员工下拉选择即可；工号选填，填了会与姓名做一致性校验。</li>
                <li>已存在的 (评价人 + 被评价人 + 评价关系) <strong>会跳过</strong>，不会清空评价人已填的草稿与分数。</li>
                <li>导入目标批次：<Tag color="blue">{batchName || '请先选择批次'}</Tag></li>
              </ul>
            }
          />
          <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>
            {batchId ? '下载导入模版（含当前批次已有关系）' : '下载导入模版'}
          </Button>
          <Upload.Dragger
            maxCount={1}
            beforeUpload={() => false}
            fileList={file ? [file] : []}
            onChange={(info) => setFile(info.fileList[info.fileList.length - 1] ?? null)}
            accept=".xls,.xlsx"
          >
            <p className="ant-upload-drag-icon"><UploadOutlined /></p>
            <p>点击或拖拽 .xls / .xlsx 文件到此处</p>
          </Upload.Dragger>
          {result && (
            <>
              <Alert
                type={result.errors.length ? 'warning' : result.pending.length ? 'info' : 'success'}
                showIcon
                message={`新增 ${result.created} / 已存在跳过 ${result.skipped} / 共 ${result.total} 行${
                  result.pending.length ? ` / 待确认 ${result.pending.length}` : ''
                }${result.errors.length ? ` / 错误 ${result.errors.length}` : ''}`}
                description={result.errors.length ? (
                  <div style={{ maxHeight: 200, overflow: 'auto', fontSize: 12 }}>
                    {result.errors.map((error, index) => (
                      <div key={index}>第 {error.row} 行：{error.message}</div>
                    ))}
                  </div>
                ) : undefined}
              />
              {result.pending.length > 0 && (
                <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, background: '#f8fafc' }}>
                  <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                    刷新或离开页面将丢失待确认列表，可重新导入同一文件继续处理。
                  </Typography.Text>
                  <Space direction="vertical" style={{ width: '100%' }} size={10}>
                    {result.pending.map((item, index) => {
                      const key = `${item.rowNo}-${item.slot}`;
                      return (
                        <div key={key} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', paddingBottom: 8, borderBottom: index < result.pending.length - 1 ? '1px dashed #e2e8f0' : undefined }}>
                          <Typography.Text style={{ minWidth: 180 }}>
                            第 {item.rowNo} 行 · {item.slot === 'evaluator' ? '评价人' : '被评价人'}：{item.name}
                          </Typography.Text>
                          <Select
                            size="small"
                            style={{ minWidth: 390 }}
                            placeholder="选择员工"
                            value={selections[key]}
                            onChange={(value) => setSelections((previous) => ({ ...previous, [key]: value }))}
                            options={item.candidates.map((candidate) => ({
                              value: candidate.empId,
                              label: `${candidate.empNo} · ${candidate.name} · ${candidate.position || '—'} · ${candidate.deptPath || '—'} · ${candidate.status || '—'}`,
                            }))}
                          />
                          <Button size="small" type="primary" loading={resolveMutation.isPending} onClick={() => resolveOne(item)}>确认</Button>
                        </div>
                      );
                    })}
                  </Space>
                </div>
              )}
            </>
          )}
        </Space>
      </Modal>
    </>
  );
}
